import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, AfterViewInit, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormControl } from '@angular/forms';
import * as L from 'leaflet';
import { Request, DepartmentType, RequestSubCategoryType, RequestCategoryType, StatusType } from 'src/app/request-v2/models/request.model';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';
import { UserService } from 'src/app/user/service/user.abstract.service';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { ProfileResponse } from 'src/app/user/user.model';
import { take, takeUntil } from 'rxjs/operators';
import { MAP_TILE_ATTRIBUTION, MAP_TILE_URL } from 'src/app/global/consts/map-tiles.const';
import { SubjectSelectComponent } from '../../../shared/subject-select/subject-select.component';
import { CategorySelectComponent } from '../../../shared/category-select/category-select.component';
import { LinkedRequestsStatusDialogComponent } from '../../../linked-requests-status-dialog/linked-requests-status-dialog.component';

@Component({
    selector: 'app-request-info',
    templateUrl: './request-info.component.html',
    styleUrls: ['./request-info.component.scss'],
})
export class RequestInfoComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
    @ViewChild('subjectSelect') subjectSelectComponent: SubjectSelectComponent;
    @ViewChild(CategorySelectComponent) categorySelectComponent: CategorySelectComponent;
    @ViewChild('descTextarea') private descTextareaRef: ElementRef<HTMLTextAreaElement>;

    @Input() request: Request;
    @Input() canEdit = false;

    @Output() statusUpdated = new EventEmitter<Request>();
    @Output() departmentUpdated = new EventEmitter<Request>();
    @Output() subCategoryUpdated = new EventEmitter<Request>();
    @Output() categoryUpdated = new EventEmitter<Request>();
    @Output() tagsUpdated = new EventEmitter<Request>();
    @Output() descriptionUpdated = new EventEmitter<Request>();

    descriptionCtrl = new FormControl('');
    editingDescription = false;
    savingDescription = false;

    departmentTypes: DepartmentType[] = [];
    allCategories: RequestCategoryType[] = [];
    isMapOpen = false;

    private map: L.Map;
    private defaultLocation: [number, number] = [45.5017, -73.5673];
    private defaultZoom = 13;
    private zoomThreshold = 14;
    private showMarkerText = true;
    private marker: L.Marker;
    private mapInitialized = false;
    private mapInitAttempts = 0;
    private maxInitAttempts = 5;

    pendingCategoryChange = false;
    tempCategory: RequestCategoryType | null = null;
    currentLocale: string;
    requesterPhone = '';
    private langChangeSubscription: Subscription;

    constructor(
        private translateService: TranslateService,
        private requestsService: RequestsV2ImplService,
        private userService: UserService,
        private dialog: MatDialog,
        private messageService: MessageService,
    ) {}

    ngOnInit(): void {
        this.loadDepartments();
        this.loadCategories();
        this.setLocale(this.translateService.currentLang);
        this.langChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.setLocale(event.lang);
        });
    }

    private setLocale(lang: string): void {
        this.currentLocale = lang === 'fr' ? 'fr-CA' : 'en-CA';
    }

    ngAfterViewInit(): void {
        // Map initializes on demand via toggleMap()
    }

    hasValidLocation(): boolean {
        return !!(this.request?.location?.geometry?.coordinates && Array.isArray(this.request.location.geometry.coordinates) && this.request.location.geometry.coordinates.length >= 2);
    }

    toggleMap(): void {
        this.isMapOpen = !this.isMapOpen;
        if (this.isMapOpen) {
            if (this.map) {
                this.map.remove();
                this.map = null;
                this.mapInitialized = false;
            }
            this.mapInitAttempts = 0;
            setTimeout(() => this.initMapWithRetry(), 50);
        } else {
            if (this.map) {
                this.map.remove();
                this.map = null;
                this.mapInitialized = false;
            }
        }
    }

    private destroy$ = new Subject<void>();
    private isUpdatingStatus = false;

    onStatusChange(event: { requestId: string; statusId: string; status: StatusType }): void {
        if (!this.request || this.request._id !== event.requestId) return;
        if (this.isUpdatingStatus) return;

        this.isUpdatingStatus = true;
        const previousState = this.request.state;
        this.request.state = event.statusId;
        this.statusUpdated.emit(this.request);

        this.requestsService
            .getRequestDetails(this.request._id)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: (updatedRequest: Request) => {
                    this.isUpdatingStatus = false;
                    this.request = updatedRequest;
                    this.statusUpdated.emit(this.request);

                    const eligibleLinks = (updatedRequest.links || []).filter((link) => link.linkedRequest && link.linkedRequest.state !== event.statusId);

                    if (eligibleLinks.length === 0) return;

                    const ref = this.dialog.open(LinkedRequestsStatusDialogComponent, {
                        data: { newStatus: event.status, eligibleLinks },
                        panelClass: 'linked-status-dialog-panel',
                        autoFocus: false,
                    });

                    ref.afterClosed()
                        .pipe(take(1))
                        .subscribe((selectedIds: string[] | null) => {
                            if (!selectedIds?.length) return;
                            selectedIds.forEach((id) => this.requestsService.updateRequestStatus(id, event.statusId).pipe(take(1)).subscribe());
                        });
                },
                error: () => {
                    this.isUpdatingStatus = false;
                    this.request.state = previousState;
                    this.statusUpdated.emit(this.request);
                },
            });
    }

    onSubCategoryChange(subCategory: RequestSubCategoryType): void {
        if (this.request) {
            const previousSubCategory = this.request.subCategory;
            this.request.subCategory = subCategory;
            this.subCategoryUpdated.emit(this.request);

            this.requestsService
                .getRequestDetails(this.request._id)
                .pipe(take(1))
                .subscribe({
                    next: (updatedRequest: Request) => {
                        this.request = updatedRequest;
                        this.departmentUpdated.emit(this.request);
                        this.subCategoryUpdated.emit(this.request);
                    },
                    error: () => {
                        this.request.subCategory = previousSubCategory;
                        this.subCategoryUpdated.emit(this.request);
                    },
                });
        }
    }

    get canSaveDescription(): boolean {
        const value = (this.descriptionCtrl.value ?? '').trim();
        return !this.savingDescription && !!value && value !== (this.request?.description ?? '').trim();
    }

    startEditDescription(): void {
        this.descriptionCtrl.setValue(this.request?.description || '');
        this.editingDescription = true;
        setTimeout(() => this.descTextareaRef?.nativeElement?.focus(), 0);
    }

    cancelEditDescription(): void {
        this.editingDescription = false;
        this.descriptionCtrl.setValue('');
    }

    saveDescription(): void {
        if (!this.canSaveDescription) return;
        this.savingDescription = true;
        const value = this.descriptionCtrl.value.trim();
        this.requestsService
            .updateDescription(this.request._id, value)
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    // The PATCH response is not populated (category, state...), so only the description is taken from it
                    this.request.description = value;
                    this.descriptionUpdated.emit(this.request);
                    this.editingDescription = false;
                    this.savingDescription = false;
                },
                error: () => {
                    this.savingDescription = false;
                    this.messageService.error(this.translateService.instant('request_v2.info.description_edit_error'));
                },
            });
    }

    onOpenSubjectMenu(category: RequestCategoryType): void {
        if (!category) return;
        this.pendingCategoryChange = true;
        this.tempCategory = category;
    }

    onSubCategoryChangeForCategory(subCategory: RequestSubCategoryType): void {
        if (!this.pendingCategoryChange || !this.tempCategory) return;
        if (this.categorySelectComponent) {
            this.categorySelectComponent.confirmCategoryChange(subCategory);
        }
        this.pendingCategoryChange = false;
        this.tempCategory = null;
    }

    onSubjectMenuClosed(): void {
        if (this.pendingCategoryChange && this.tempCategory) {
            if (this.categorySelectComponent) {
                this.categorySelectComponent.cancelCategoryChange();
            }
            this.pendingCategoryChange = false;
            this.tempCategory = null;
        }
    }

    onCategoryAndSubjectChange(data: { category: RequestCategoryType; subject: RequestSubCategoryType }): void {
        if (!this.request) return;
        const previousCategory = this.request.category;
        const previousSubCategory = this.request.subCategory;
        this.request.category = data.category;
        this.request.subCategory = data.subject;
        this.categoryUpdated.emit(this.request);
        this.subCategoryUpdated.emit(this.request);

        this.requestsService
            .getRequestDetails(this.request._id)
            .pipe(take(1))
            .subscribe({
                next: (updatedRequest: Request) => {
                    this.request = updatedRequest;
                    this.statusUpdated.emit(this.request);
                },
                error: () => {
                    this.request.category = previousCategory;
                    this.request.subCategory = previousSubCategory;
                    this.categoryUpdated.emit(this.request);
                    this.subCategoryUpdated.emit(this.request);
                },
            });
    }

    onRevertChanges(): void {
        if (!this.request) return;
        this.pendingCategoryChange = false;
        this.tempCategory = null;
        if (this.subjectSelectComponent) {
            this.subjectSelectComponent.selectedSubject = this.request.subCategory;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.request) {
            const previousUserId = changes.request.previousValue?.user?._id;
            const requestedUserId = this.request?.user?._id;

            if (previousUserId !== requestedUserId) {
                this.requesterPhone = '';

                if (requestedUserId) {
                    this.userService
                        .getProfileById(requestedUserId)
                        .pipe(take(1))
                        .subscribe({
                            next: (profile: ProfileResponse) => {
                                if (this.request?.user?._id !== requestedUserId) {
                                    return;
                                }
                                this.requesterPhone = profile?.cellPhone || profile?.phone || '';
                            },
                            error: () => {
                                if (this.request?.user?._id === requestedUserId) {
                                    this.requesterPhone = '';
                                }
                            },
                        });
                }
            }
        }

        if (changes.request && this.map && this.request) {
            this.updateMarker();
        }
    }

    getInitials(name: string): string {
        if (!name) return '';
        const parts = name.trim().split(' ');
        if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
        return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    }

    formatAddressLine1(location: any): string {
        if (!location) return '';
        const addr = location.address;
        if (!addr) return '';
        if (typeof addr === 'string') return addr;
        const parts: string[] = [];
        if (addr.unit) parts.push(`App. ${addr.unit}`);
        if (addr.number) parts.push(String(addr.number));
        if (addr.streetName) parts.push(addr.streetName);
        return parts.join(' ');
    }

    formatAddressLine2(location: any): string {
        if (!location) return '';
        const addr = location.address;
        if (!addr || typeof addr === 'string') return '';
        const city = addr.city ? String(addr.city).trim() : '';
        const postalCode = this.formatCanadianPostalCode(addr.postCode);
        if (city && postalCode) return `${city}, ${postalCode}`;
        return city || postalCode;
    }

    private formatCanadianPostalCode(postalCode: any): string {
        if (!postalCode) return '';
        const normalized = String(postalCode)
            .replace(/[^a-zA-Z0-9]/g, '')
            .toUpperCase();
        if (normalized.length !== 6) return String(postalCode).trim().toUpperCase();
        return `${normalized.slice(0, 3)} ${normalized.slice(3)}`;
    }

    formatAddress(location: any): string {
        if (!location) return '';

        if (typeof location.address === 'string') {
            return location.address;
        }

        if (location.address && typeof location.address === 'object') {
            const addr = location.address;
            const parts = [];
            let streetPart = '';
            if (addr.number) streetPart += `${addr.number} `;
            if (addr.streetName) streetPart += addr.streetName;
            else if (addr.city) streetPart += addr.city;
            if (streetPart) parts.push(streetPart);
            if (addr.city && addr.streetName) parts.push(addr.city);
            if (addr.province) parts.push(addr.province);
            return parts.join(', ');
        }

        return '';
    }

    findStatus(statusId: string) {
        if (!this.request || !this.request.category || !this.request.category.statuses) {
            return null;
        }
        return this.request.category.statuses.find((status) => status._id === statusId);
    }

    private initMapWithRetry(): void {
        if (this.initializeMap()) return;

        const retryInterval = setInterval(() => {
            this.mapInitAttempts++;
            if (this.mapInitialized || this.mapInitAttempts >= this.maxInitAttempts) {
                clearInterval(retryInterval);
                return;
            }
            this.initializeMap();
        }, 100);
    }

    private initializeMap(): boolean {
        if (this.mapInitialized && this.map) return true;

        const mapElement = document.getElementById('request-detail-map');
        if (!mapElement) return false;

        try {
            if (this.map) this.map.remove();

            let initialLocation = this.defaultLocation;
            if (this.hasValidLocation()) {
                initialLocation = [this.request.location.geometry.coordinates[1], this.request.location.geometry.coordinates[0]];
            }

            this.map = L.map('request-detail-map', { zoomControl: false }).setView(initialLocation, this.defaultZoom);
            L.control.zoom({ position: 'topright' }).addTo(this.map);
            L.tileLayer(MAP_TILE_URL, {
                maxZoom: 18,
                minZoom: 13,
                attribution: MAP_TILE_ATTRIBUTION,
            }).addTo(this.map);

            this.showMarkerText = this.map.getZoom() >= this.zoomThreshold;
            this.map.on('zoomend', () => {
                const newShow = this.map.getZoom() >= this.zoomThreshold;
                if (newShow !== this.showMarkerText) {
                    this.showMarkerText = newShow;
                    this.updateMarkerIcon();
                }
            });

            this.map.invalidateSize();
            if (this.request) this.addMarkerToMap();

            this.mapInitialized = true;
            return true;
        } catch (error) {
            return false;
        }
    }

    private updateMarker(): void {
        if (this.marker) this.map.removeLayer(this.marker);
        this.addMarkerToMap();
    }

    private addMarkerToMap(): void {
        if (!this.map || !this.request) return;

        let latLng: L.LatLngExpression = this.defaultLocation;
        if (this.hasValidLocation()) {
            latLng = [this.request.location.geometry.coordinates[1], this.request.location.geometry.coordinates[0]];
        }

        const markerColor = this.request.category?.color || this.request.department?.color || '#333333';
        const requestText = this.translateService.instant('request_v2.info.request');

        const markerIcon = this.createCustomMarkerIcon(this.request.category?.icon || 'fas fa-map-marker-alt', this.request.category?.name || requestText, this.request.requestId || '', markerColor);

        this.marker = L.marker(latLng, { icon: markerIcon }).addTo(this.map);
        this.map.setView(latLng, this.defaultZoom);
    }

    private createCustomMarkerIcon(iconClass: string, categoryText: string, idText: string, color: string): L.DivIcon {
        const colors = {
            fill: this.getMarkerBackgroundColor(color),
            stroke: color,
        };

        const markerHtml = `
            <div class="pin-container">
                <div class="marker-content">
                    <div class="svg-container">
                        <svg viewBox="0 0 26 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g id="Vector" filter="url(#filter0_d_306_3188)">
                                <path d="M12.9543 1C17.505 1 20.9086 5.01135 20.9086 10.1343C20.9086 14.7536 16.8923 20.6793 13.617 23.436C13.2261 23.7638 12.6825 23.7638 12.2916 23.436C9.01714 20.6813 5 14.7536 5 10.1343C5 5.01135 8.40352 1 12.9543 1Z" fill="${colors.fill}"/>
                                <path d="M13.9383 23.8191L13.9389 23.8185C15.626 22.3986 17.484 20.1822 18.922 17.7371C20.3555 15.2996 21.4086 12.5723 21.4086 10.1343C21.4086 4.80632 17.8473 0.5 12.9543 0.5C8.06127 0.5 4.5 4.80632 4.5 10.1343C4.5 12.5723 5.55328 15.3002 6.98688 17.7378C8.42498 20.1832 10.283 22.3996 11.9697 23.8186L11.9703 23.8191C12.547 24.3027 13.3615 24.3027 13.9383 23.8191Z" stroke="${colors.stroke}"/>
                            </g>
                            <defs>
                                <filter id="filter0_d_306_3188" x="0" y="0" width="25.9086" height="32.6819" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
                                    <feFlood flood-opacity="0" result="BackgroundImageFix"/>
                                    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                                    <feOffset dy="4"/>
                                    <feGaussianBlur stdDeviation="2"/>
                                    <feComposite in2="hardAlpha" operator="out"/>
                                    <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
                                    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_306_3188"/>
                                    <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_306_3188" result="shape"/>
                                </filter>
                            </defs>
                        </svg>
                        <i class="${iconClass} marker-icon" style="color: ${colors.stroke};"></i>
                    </div>
                    ${
                        this.showMarkerText
                            ? `<div class="pin-text">
                        <div class="category-text" style="color: #000000; font-size: 14px;">${categoryText}</div>
                        <div class="id-text" style="color: #000000; font-size: 13px;">${idText}</div>
                    </div>`
                            : ''
                    }
                </div>
            </div>
        `;

        return L.divIcon({
            html: markerHtml,
            className: 'custom-div-icon',
            iconSize: [250, 60],
            iconAnchor: [20, 50],
        });
    }

    private updateMarkerIcon(): void {
        if (!this.map || !this.marker || !this.request) return;
        const markerColor = this.request.category?.color || this.request.department?.color || '#333333';
        const requestText = this.translateService.instant('request_v2.info.request');
        const markerIcon = this.createCustomMarkerIcon(this.request.category?.icon || 'fas fa-map-marker-alt', this.request.category?.name || requestText, this.request.requestId || '', markerColor);
        this.marker.setIcon(markerIcon);
    }

    getMarkerBackgroundColor(color: string): string {
        if (!color) return '#f5f5f5';
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const f = 0.8;
        const r2 = Math.round(r + (255 - r) * f);
        const g2 = Math.round(g + (255 - g) * f);
        const b2 = Math.round(b + (255 - b) * f);
        return `#${((1 << 24) + (r2 << 16) + (g2 << 8) + b2).toString(16).slice(1)}`;
    }

    getChipBackgroundColor(color: string, opacity = 0.15): string {
        if (!color) return 'rgba(0, 0, 0, 0.05)';
        const hex = color.replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    }

    loadDepartments(): void {
        this.requestsService.getDepartments().subscribe(
            (departments) => {
                this.departmentTypes = departments?.length ? departments : [];
            },
            () => {
                this.departmentTypes = [];
            },
        );
    }

    loadCategories(): void {
        this.requestsService.getRequestCategories().subscribe(
            (categories) => {
                this.allCategories = categories?.length ? (categories as any[]) : [];
            },
            () => {
                this.allCategories = [];
            },
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.isUpdatingStatus = false;
        if (this.map) {
            this.map.remove();
            this.mapInitialized = false;
        }
        if (this.langChangeSubscription) {
            this.langChangeSubscription.unsubscribe();
        }
    }
}
